
export interface BridgeData {
    bank?: string;
    regId?: string;
    [key: string]: any; 
  }
  
  /**
   * The structure stored in localStorage: { bankName: BridgeData }
   */
  interface LocalBridgeData {
    [key: string]: BridgeData;
  }

  /**
   * Configuration for polling behavior
   */
  export interface PollingConfig {
    maxAttempts?: number;      // Maximum number of polling attempts (default: 40)
    intervalMs?: number;        // Interval between attempts in milliseconds (default: 500)
    timeoutMs?: number;         // Total timeout in milliseconds (default: 20000)
  }

  /**
   * Result of polling operation
   */
  export interface PollingResult {
    success: boolean;
    data: LocalBridgeData | null;
    error: string | null;
    attempts: number;
  }
  
  const BRIDGE_DATA_STORAGE_KEY = "unifiedBankBridgeData";
  const LEGACY_BRIDGE_DATA_KEY = "bridgeData";
   
  /**
   * Reads and parses the unified bridge data from localStorage.
   * @returns {LocalBridgeData} The parsed bridge data object, or an empty object.
   */
  const getLocalBridgeData = (): LocalBridgeData => {
    try {
      const dataString = localStorage.getItem(BRIDGE_DATA_STORAGE_KEY);
      return dataString ? JSON.parse(dataString) : {};
    } catch (e) {
      console.error("Error parsing unified bank bridge data:", e);
      return {};
    }
  };
  
  /**
   * Saves the unified bridge data back to localStorage.
   * @param {LocalBridgeData} data The bridge data object to save.
   */
  const setLocalBridgeData = (data: LocalBridgeData): void => {
    try {
      localStorage.setItem(BRIDGE_DATA_STORAGE_KEY, JSON.stringify(data));
    } catch (e) {
      console.error("Error setting unified bank bridge data:", e);
    }
  };
  
  /**
   * Updates the bridge data for a specific bank in localStorage, merging with existing data.
   * This is the function that creates the segregated storage.
   * * @param {BridgeData} newData The new bridge data object (must contain a 'bank' property).
   * @returns {void}
   */
  
  export const updateBankBridgeData = (newData: BridgeData): void => {
    if (!newData.bank) {
      console.error("Cannot update bridge data: 'bank' key is missing in the data.");
      return;
    }
    
    const bankKey = newData.bank.toLowerCase();
    const currentData = getLocalBridgeData();
    
    // Merge new data with existing data for the bank
    currentData[bankKey] = {
      ...(currentData[bankKey] || {}), // Start with existing data
      ...newData,                      // Overlay with new data
      bank: bankKey                    // Ensure bank key is lowercase and present
    };
    setLocalBridgeData(currentData);
  };
  
  /**
   * Retrieves the bridge data for a specific bank from the segregated storage.
   * @param {string | undefined} bankName The bank's name (e.g., 'kotak', 'axis').
   * @returns {BridgeData} The bridge data for the specified bank, or an empty object.
   */
  export const getBankBridgeData = (bankName: string | undefined): BridgeData => {
    if (!bankName) return {};
    const key = bankName.toLowerCase();
    return getLocalBridgeData()[key] || {};
  };

  /**
   * Retrieves all bank bridge data from localStorage.
   * @returns {LocalBridgeData} All stored bank bridge data.
   */
  export const getAllBankBridgeData = (): LocalBridgeData => {
    return getLocalBridgeData();
  };

  /**
   * Checks if bridge data exists in localStorage (either legacy or new format).
   * @returns {boolean} True if bridge data exists, false otherwise.
   */
  export const hasBridgeData = (): boolean => {
    // Check new unified format
    const unifiedData = getLocalBridgeData();
    if (Object.keys(unifiedData).length > 0) {
      return true;
    }

    // Check legacy format
    const legacyData = localStorage.getItem(LEGACY_BRIDGE_DATA_KEY);
    if (legacyData) {
      try {
        const parsed = JSON.parse(legacyData);
        return Object.keys(parsed).length > 0;
      } catch (e) {
        return false;
      }
    }

    return false;
  };

  /**
   * Migrates legacy bridgeData format to the new unified format.
   * This ensures backward compatibility.
   * @returns {boolean} True if migration was performed, false otherwise.
   */
  export const migrateLegacyBridgeData = (): boolean => {
    try {
      const legacyDataString = localStorage.getItem(LEGACY_BRIDGE_DATA_KEY);
      if (!legacyDataString) {
        return false;
      }

      const legacyData = JSON.parse(legacyDataString);
      
      // Check if it has a bank identifier
      if (legacyData.bank) {
        console.log("[localStorageManage] Migrating legacy bridgeData to unified format");
        updateBankBridgeData(legacyData);
        return true;
      }

      return false;
    } catch (e) {
      console.error("Error migrating legacy bridge data:", e);
      return false;
    }
  };

  /**
   * Polls localStorage for bridge data availability with timeout.
   * This is used during initialization when data might not be immediately available.
   * 
   * @param {PollingConfig} config Configuration for polling behavior
   * @returns {Promise<PollingResult>} Promise that resolves with polling result
   */
  export const pollForBridgeData = async (
    config: PollingConfig = {}
  ): Promise<PollingResult> => {
    const {
      maxAttempts = 40,
      intervalMs = 500,
      timeoutMs = 20000
    } = config;

    const startTime = Date.now();
    let attempts = 0;

    console.log(`[localStorageManage] Starting bridge data polling (max ${maxAttempts} attempts, ${timeoutMs}ms timeout)`);

    return new Promise((resolve) => {
      const checkData = () => {
        attempts++;
        const elapsed = Date.now() - startTime;

        // Check for timeout
        if (elapsed >= timeoutMs) {
          console.error("[localStorageManage] Polling timed out");
          resolve({
            success: false,
            data: null,
            error: "Timeout: Bridge data not received within the expected timeframe",
            attempts
          });
          return;
        }

        // Check for max attempts
        if (attempts >= maxAttempts) {
          console.error("[localStorageManage] Maximum polling attempts reached");
          resolve({
            success: false,
            data: null,
            error: "Maximum polling attempts exceeded",
            attempts
          });
          return;
        }

        // Migrate legacy data if it exists
        migrateLegacyBridgeData();

        // Check if data is available
        if (hasBridgeData()) {
          const data = getLocalBridgeData();
          console.log(`[localStorageManage] Bridge data found after ${attempts} attempts (${elapsed}ms)`);
          resolve({
            success: true,
            data,
            error: null,
            attempts
          });
          return;
        }

        // Continue polling
        console.log(`[localStorageManage] Polling attempt ${attempts}/${maxAttempts} (${elapsed}ms elapsed)`);
        setTimeout(checkData, intervalMs);
      };

      // Start immediate check
      checkData();
    });
  };
  
  /**
   * Processes the raw bridge data received and stores it correctly using the utility.
   * This is what you would call in your useMemo/useEffect block.
   * @param {BridgeData} rawBridgeData The raw data received.
   * @returns {BridgeData} The cleaned and updated context for the current session.
   */
  export const processAndStoreBridgeData = (rawBridgeData: BridgeData): BridgeData => {
      // 1. Ensure the bank key exists and is lowercase
      if (rawBridgeData.bank) {
          rawBridgeData.bank = rawBridgeData.bank.toLowerCase();
      } else {
          return {}; // Cannot proceed without a bank identifier
      }
  
      // 2. Update the segregated store
      updateBankBridgeData(rawBridgeData);
  
      // 3. Return the data for the current session's context
      return rawBridgeData;
  };

  /**
   * Clears all bridge data from localStorage.
   * Useful for logout scenarios.
   */
  export const clearAllBridgeData = (): void => {
    try {
      localStorage.removeItem(BRIDGE_DATA_STORAGE_KEY);
      localStorage.removeItem(LEGACY_BRIDGE_DATA_KEY);
      console.log("[localStorageManage] Cleared all bridge data");
    } catch (e) {
      console.error("Error clearing bridge data:", e);
    }
  };

  /**
   * Clears bridge data for a specific bank.
   * @param {string} bankName The bank's name.
   */
  export const clearBankBridgeData = (bankName: string): void => {
    if (!bankName) return;
    
    try {
      const key = bankName.toLowerCase();
      const currentData = getLocalBridgeData();
      delete currentData[key];
      setLocalBridgeData(currentData);
      console.log(`[localStorageManage] Cleared bridge data for bank: ${key}`);
    } catch (e) {
      console.error(`Error clearing bridge data for bank ${bankName}:`, e);
    }
  };